<template>
  <nav class="navbar">
    <div class="nav-container" :class="{ 'homepage-nav': isHomePage }">
      <!-- Logo/Brand - Only show on non-homepage -->
      <RouterLink 
        v-if="!isHomePage" 
        to="/" 
        class="nav-logo"
      >
        <span class="brand-line">
          <span class="large-cap">P</span>OSE <span class="large-cap">P</span>ERFECT
        </span>
        <span class="logo-subtitle">Photography</span>
      </RouterLink>

      <!-- Desktop Menu -->
      <div class="nav-menu" :class="{ 'nav-menu-active': mobileMenuOpen, 'nav-menu-full': isHomePage }">
        <div 
          v-for="item in menuItems" 
          :key="item.name"
          class="nav-item"
          :class="{ 'nav-item-active': isActiveMenuItem(item) }"
          @mouseenter="showSubmenu(item.name)"
          @mouseleave="hideSubmenu"
        >
          <RouterLink 
            v-if="!item.children && item.path" 
            :to="item.path" 
            class="nav-link"
            @click="closeMobileMenu"
          >
            {{ item.name }}
          </RouterLink>
          
          <div v-else class="nav-dropdown">
            <span 
              class="nav-link dropdown-trigger"
              @click="toggleSubmenu(item.name)"
            >
              {{ item.name }}
              <i class="dropdown-arrow">▼</i>
            </span>
            
            <!-- Submenu -->
            <div 
              class="dropdown-menu" 
              :class="{ 'dropdown-active': activeSubmenu === item.name }"
            >
              <div 
                v-for="subItem in item.children" 
                :key="subItem.name"
                class="dropdown-item"
              >
                <RouterLink 
                  v-if="!subItem.children && subItem.path"
                  :to="subItem.path" 
                  class="dropdown-link"
                  @click="closeMobileMenu"
                >
                  {{ subItem.name }}
                </RouterLink>
                
                <!-- Sub-submenu -->
                <div v-else class="dropdown-nested">
                  <span class="dropdown-link nested-trigger">
                    {{ subItem.name }}
                    <i class="dropdown-arrow">▶</i>
                  </span>
                  <div class="dropdown-nested-menu">
                    <RouterLink 
                      v-for="nestedItem in subItem.children"
                      :key="nestedItem.name"
                      :to="nestedItem.path || '/'"
                      class="dropdown-link"
                      @click="closeMobileMenu"
                    >
                      {{ nestedItem.name }}
                    </RouterLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Hamburger -->
      <div class="mobile-menu-toggle" @click="toggleMobileMenu">
        <span class="hamburger-line" :class="{ active: mobileMenuOpen }"></span>
        <span class="hamburger-line" :class="{ active: mobileMenuOpen }"></span>
        <span class="hamburger-line" :class="{ active: mobileMenuOpen }"></span>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { settingsService } from '@/services/settingsService'

interface MenuItem {
  name: string
  path?: string
  children?: MenuItem[]
}

const route = useRoute()
const mobileMenuOpen = ref(false)
const activeSubmenu = ref<string | null>(null)

const isHomePage = computed(() => route.path === '/')
const settings = settingsService.getSettings()

const menuItems: MenuItem[] = [
  { 
    name: 'Portfolio', 
    children: [
      { 
        name: 'Weddings', 
        children: [
          ...(settings.categories.weddings ? [{ name: 'Wedding Photography', path: '/portfolio/weddings' }] : []),
          ...(settings.categories.bridal ? [{ name: 'Bridal', path: '/portfolio/bridal' }] : [])
        ].filter(item => item) // Remove empty items
      },
      { 
        name: 'Portraits', 
        children: [
          ...(settings.categories.seniors ? [{ name: 'Seniors', path: '/portfolio/seniors' }] : []),
          ...(settings.categories.graduation ? [{ name: 'Graduation', path: '/portfolio/graduation' }] : []),
          ...(settings.categories.headshots ? [{ name: 'Headshots', path: '/portfolio/headshots' }] : []),
          ...(settings.categories.maternity ? [{ name: 'Maternity', path: '/portfolio/maternity' }] : []),
          ...(settings.categories.creative ? [{ name: 'Creative Concept', path: '/portfolio/creative' }] : [])
        ].filter(item => item)
      },
      ...(settings.categories.fashion ? [{ name: 'Fashion', path: '/portfolio/fashion' }] : []),
      ...(settings.categories.modeling ? [{ name: 'Model Portfolio Development', path: '/portfolio/modeling' }] : [])
    ].filter(item => !item.children || item.children.length > 0) // Remove empty parent categories
  },
  ...(settings.menu.showContact ? [{ name: 'Contact', path: '/contact' }] : []),
  ...(settings.menu.showClients ? [{ name: 'Clients', path: '/login' }] : [])
].filter(item => item)

const isActiveMenuItem = (item: MenuItem): boolean => {
  if (item.path && route.path === item.path) {
    return true
  }
  
  if (item.children) {
    return item.children.some(child => {
      if (child.path && route.path === child.path) {
        return true
      }
      if (child.children) {
        return child.children.some(nested => nested.path === route.path)
      }
      return false
    })
  }
  
  return false
}

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

const toggleSubmenu = (itemName: string) => {
  if (activeSubmenu.value === itemName) {
    activeSubmenu.value = null
  } else {
    activeSubmenu.value = itemName
  }
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
  activeSubmenu.value = null
}

const showSubmenu = (itemName: string) => {
  if (window.innerWidth >= 768) {
    activeSubmenu.value = itemName
  }
}

const hideSubmenu = () => {
  if (window.innerWidth >= 768) {
    activeSubmenu.value = null
  }
}
</script>

<style scoped>
.navbar {
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.3s ease;
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
}

.nav-menu {
  display: flex;
  align-items: center;
  gap: 0;
}

.nav-menu-full {
  width: 100%;
  justify-content: center;
}

.homepage-nav {
  justify-content: space-between;
}

.homepage-nav .nav-menu {
  flex: 1;
  justify-content: center;
}

.nav-logo {
  font-size: 1.2rem;
  font-weight: bold;
  color: white;
  text-decoration: none;
  transition: color 0.3s ease;
  text-transform: uppercase;
  font-family: "eloquent-jf-pro", 'Playfair Display', 'Times New Roman', serif;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.1;
  max-width: 100%;
}

.brand-line {
  white-space: nowrap;
  display: block;
}

.large-cap {
  font-size: 1.2em;
  font-weight: 700;
}

.logo-subtitle {
  font-size: 0.6em;
  font-style: italic;
  margin-left: 8em;
  opacity: 0.9;
  text-transform: none;
  white-space: nowrap;
}

.nav-logo:hover {
  color: #cccccc;
}

.nav-item {
  position: relative;
}

.nav-item-active > .nav-link,
.nav-item-active > .nav-dropdown > .nav-link {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.15);
  font-weight: 600;
}

.nav-link {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  color: white;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
}

.nav-link:hover {
  color: #cccccc;
  background: rgba(255, 255, 255, 0.1);
}

.dropdown-trigger {
  user-select: none;
}

.dropdown-arrow {
  margin-left: 0.5rem;
  font-size: 0.8rem;
  transition: transform 0.3s ease;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  min-width: 200px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.3s ease;
  z-index: 1001;
}

.dropdown-active {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-item {
  position: relative;
}

.dropdown-link {
  display: block;
  padding: 0.75rem 1.5rem;
  color: white;
  text-decoration: none;
  transition: all 0.3s ease;
  border-radius: 6px;
  margin: 0.25rem;
}

.dropdown-link:hover {
  background: rgba(255, 255, 255, 0.15);
  color: white;
}

.dropdown-nested {
  position: relative;
}

.nested-trigger {
  cursor: pointer;
  user-select: none;
}

.dropdown-nested-menu {
  position: absolute;
  top: 0;
  left: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  min-width: 180px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  opacity: 0;
  visibility: hidden;
  transform: translateX(-10px);
  transition: all 0.3s ease;
  z-index: 1002;
}

.dropdown-nested:hover .dropdown-nested-menu {
  opacity: 1;
  visibility: visible;
  transform: translateX(0);
}

.mobile-menu-toggle {
  display: none;
  flex-direction: column;
  cursor: pointer;
  padding: 0.5rem;
}

.hamburger-line {
  width: 25px;
  height: 3px;
  background: white;
  margin: 3px 0;
  transition: all 0.3s ease;
  border-radius: 2px;
}

.hamburger-line.active:nth-child(1) {
  transform: rotate(45deg) translate(6px, 6px);
}

.hamburger-line.active:nth-child(2) {
  opacity: 0;
}

.hamburger-line.active:nth-child(3) {
  transform: rotate(-45deg) translate(6px, -6px);
}

/* Mobile Styles */
@media (max-width: 768px) {
  .nav-container {
    padding: 0 1rem;
  }

  .nav-logo {
    font-size: 1rem;
    max-width: calc(100vw - 90px); /* leave room for the hamburger */
  }

  .brand-line {
    white-space: normal;
  }

  .logo-subtitle {
    margin-left: 4em;
  }

  .mobile-menu-toggle {
    display: flex;
    margin-left: auto;
  }

  .nav-menu {
    position: fixed;
    top: 70px;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.7);
    flex-direction: column;
    align-items: stretch;
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  .nav-menu-active {
    max-height: 100vh;
  }

  .nav-item {
    border-bottom: 1px solid #eee;
  }

  .nav-link {
    padding: 1rem;
    justify-content: space-between;
  }

  .dropdown-menu {
    position: static;
    box-shadow: none;
    background: rgba(0, 0, 0, 0.9);
    opacity: 1;
    visibility: visible;
    transform: none;
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease;
  }

  .dropdown-active {
    max-height: 300px;
  }

  .dropdown-nested-menu {
    position: static;
    box-shadow: none;
    background: rgba(0, 0, 0, 0.8);
    opacity: 1;
    visibility: visible;
    transform: none;
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease;
  }

  .dropdown-nested:hover .dropdown-nested-menu,
  .dropdown-nested.active .dropdown-nested-menu {
    max-height: 200px;
  }

  .dropdown-arrow {
    transform: rotate(0deg);
  }

  .dropdown-active .dropdown-arrow {
    transform: rotate(180deg);
  }
}

/* Tablet Styles */
@media (min-width: 769px) and (max-width: 1024px) {
  .nav-container {
    padding: 0 1.5rem;
  }

  .nav-link {
    padding: 1rem 1rem;
  }
}
</style>
